import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "./ui/alert-dialog";

const INITIAL_FORM_STATE = {
  Classe: '',
  Instit: '',
  Mail: '',
  telephone: '',
  Moniteur: ''
};

const GeneralInfoTab = ({ data, onUpdate, onTabChange, currentTab, requestedTab }) => {
  // États du formulaire
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [initialFormData, setInitialFormData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // États de l'interface
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // États des données externes
  const [classes, setClasses] = useState([]);
  const [moniteurs, setMoniteurs] = useState([]);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isLoadingMoniteurs, setIsLoadingMoniteurs] = useState(false);

  // Charger les classes disponibles
  const fetchClasses = useCallback(async () => {
    setIsLoadingClasses(true);
    setError(null);
    try {
      const response = await fetch('https://table.naturavelo.net/api/v2/tables/moel0ns4k7yir44/records?limit=1000', {
        headers: {
          'xc-token': 'JqpXXmIrMbKU0AUMjaMNH3zv40__Cbtf0sVkYo3a'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du chargement des classes');
      }
      
      const result = await response.json();
      const uniqueClasses = [...new Set(result.list.map(item => item.Classe))]
        .filter(Boolean)
        .sort();
      setClasses(uniqueClasses);
    } catch (err) {
      console.error('Erreur lors du chargement des classes:', err);
      setError('Impossible de charger la liste des classes. Veuillez réessayer.');
    } finally {
      setIsLoadingClasses(false);
    }
  }, []);

  // Initialiser les données du formulaire
  useEffect(() => {
    if (data) {
      const newFormData = {
        Classe: data.Classe || '',
        Instit: data.Instit || '',
        Mail: data.Mail || '',
        telephone: data.telephone || '',
        Moniteur: data.Moniteur || ''
      };
      setFormData(newFormData);
      setInitialFormData(newFormData);
    }
  }, [data]);

  // Charger les moniteurs disponibles
  const fetchMoniteurs = useCallback(async () => {
    setIsLoadingMoniteurs(true);
    setError(null);
    try {
      const response = await fetch('https://table.naturavelo.net/api/v2/tables/moel0ns4k7yir44/records?limit=1000', {
        headers: {
          'xc-token': 'JqpXXmIrMbKU0AUMjaMNH3zv40__Cbtf0sVkYo3a'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du chargement des moniteurs');
      }
      
      const result = await response.json();
      const uniqueMoniteurs = [...new Set(result.list.map(item => item.Moniteur))]
        .filter(Boolean)
        .sort();
      setMoniteurs(uniqueMoniteurs);
    } catch (err) {
      console.error('Erreur lors du chargement des moniteurs:', err);
      setError('Impossible de charger la liste des moniteurs. Veuillez réessayer.');
    } finally {
      setIsLoadingMoniteurs(false);
    }
  }, []);

  // Charger les données au montage
  useEffect(() => {
    fetchClasses();
    fetchMoniteurs();
  }, [fetchClasses, fetchMoniteurs]);

  // Détecter les changements non sauvegardés
  useEffect(() => {
    if (initialFormData) {
      const hasChanges = Object.keys(formData).some(
        key => formData[key] !== initialFormData[key]
      );
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, initialFormData]);

  // Gérer les demandes de changement d'onglet
  useEffect(() => {
    if (requestedTab && requestedTab !== currentTab && hasUnsavedChanges) {
      setShowExitDialog(true);
    } else if (requestedTab && !hasUnsavedChanges) {
      onTabChange(requestedTab);
    }
  }, [requestedTab, currentTab, hasUnsavedChanges, onTabChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!hasUnsavedChanges) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Préparation des données pour l'API
      const updatePayload = Object.entries(formData).reduce((acc, [key, value]) => {
        // Ne pas inclure les champs qui n'ont pas changé
        if (value !== initialFormData[key]) {
          acc[key] = value || null; // Convertir les chaînes vides en null
        }
        return acc;
      }, {});

      // Vérifier s'il y a des modifications à envoyer
      if (Object.keys(updatePayload).length === 0) {
        setHasUnsavedChanges(false);
        return;
      }

      await onUpdate(updatePayload);
      setInitialFormData(formData);
      setHasUnsavedChanges(false);

      // Notification de succès
      const notification = document.createElement('div');
      notification.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg transform transition-transform duration-500';
      notification.textContent = 'Informations enregistrées avec succès';
      document.body.appendChild(notification);
      setTimeout(() => {
        notification.style.transform = 'translateY(150%)';
        setTimeout(() => document.body.removeChild(notification), 500);
      }, 3000);
    } catch (err) {
      console.error('Erreur de soumission:', err);
      setError('Erreur lors de la mise à jour des informations. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmTabChange = async (shouldSave) => {
    try {
      if (shouldSave) {
        await handleSubmit();
      }
      onTabChange(requestedTab);
    } catch (err) {
      setError('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setShowExitDialog(false);
    }
  };

  const handleCancelTabChange = () => {
    setShowExitDialog(false);
  };

  return (
    <>
      <div className="bg-white p-6 rounded-xl">
        {/* Indicateur de modifications non sauvegardées */}
        {hasUnsavedChanges && (
          <div className="mb-4 p-2 bg-amber-50 text-amber-600 rounded-md flex items-center">
            <span className="mr-2">⚠️</span>
            <span>Modifications non enregistrées</span>
          </div>
        )}

        {/* Message d'erreur */}
        {error && (
          <div className="mb-4 p-2 bg-red-50 text-red-600 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sélection de la classe */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Classe
            </label>
            <div className="relative">
              <select
                name="Classe"
                value={formData.Classe}
                onChange={handleChange}
                className={`
                  w-full p-2 border border-gray-300 rounded-md
                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  ${isLoadingClasses ? 'bg-gray-50' : 'bg-white'}
                  disabled:cursor-not-allowed
                `}
                disabled={isLoadingClasses}
              >
                <option value="">Sélectionner une classe</option>
                {classes.map((classe) => (
                  <option key={classe} value={classe}>
                    {classe}
                  </option>
                ))}
              </select>
              {isLoadingClasses && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                </div>
              )}
            </div>
          </div>

          {/* Instituteur */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Instituteur/trice
            </label>
            <input
              type="text"
              name="Instit"
              value={formData.Instit}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Nom de l'instituteur/trice"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              name="Mail"
              value={formData.Mail}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="adresse@email.com"
            />
          </div>

          {/* Téléphone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone
            </label>
            <input
              type="tel"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="06 12 34 56 78"
            />
          </div>

           {/* Moniteur en charge du cycle */}
           <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Moniteur en charge du cycle
            </label>
            <div className="relative">
              <select
                name="Moniteur"
                value={formData.Moniteur}
                onChange={handleChange}
                className={`
                  w-full p-2 border border-gray-300 rounded-md
                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  ${isLoadingMoniteurs ? 'bg-gray-50' : 'bg-white'}
                  disabled:cursor-not-allowed
                `}
                disabled={isLoadingMoniteurs}
              >
                <option value="">Sélectionner un moniteur</option>
                {moniteurs.map((moniteur) => (
                  <option key={moniteur} value={moniteur}>
                    {moniteur}
                  </option>
                ))}
              </select>
              {isLoadingMoniteurs && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                </div>
              )}
            </div>
          </div>

          {/* Bouton de soumission */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting || !hasUnsavedChanges}
              className={`
                px-6 py-2 rounded-md text-white font-medium
                transition-all duration-200
                ${(isSubmitting || !hasUnsavedChanges)
                  ? 'bg-gray-400 cursor-not-allowed opacity-60'
                  : 'bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow'}
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              `}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Mise à jour en cours...
                </span>
              ) : (
                'Mettre à jour les informations'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Dialog de confirmation de changement d'onglet */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifications non enregistrées</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez des modifications non enregistrées. Voulez-vous les enregistrer avant de quitter cet onglet ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="space-x-2">
            <AlertDialogCancel 
              onClick={handleCancelTabChange}
              className="hover:bg-gray-100"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleConfirmTabChange(false)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Quitter sans enregistrer
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => handleConfirmTabChange(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Enregistrer et quitter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GeneralInfoTab;